'use client'

import { createClient as createSupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

import { SUPABASE_PUBLIC_URL, SUPABASE_PUBLIC_ANON_KEY } from '@/lib/supabase-config'

// =============================================================================
// UNIFIED SUPABASE CLIENT
// =============================================================================
// This file consolidates all Supabase client patterns into a single system.
// Replaces: supabase-lite.ts, supabase-no-realtime.ts, supabase-optimized.ts,
//           supabase-securities.ts (partially)
//
// Key features:
// - Lazy-load env vars (prevents Next.js build errors)
// - Three client types: client-side, server-side, admin
// - Optional realtime functionality (create separate client when needed)
// - Session/user caching (5-min TTL)
// - TypeScript Database types
// - 10s timeout with AbortController
// - Singleton pattern with Proxy for lazy initialization
// =============================================================================

// -----------------------------------------------------------------------------
// Environment Variable Getters (Lazy-Load)
// -----------------------------------------------------------------------------
// These prevent build-time errors by loading env vars only when needed

function getSupabaseUrl(): string {
  return SUPABASE_PUBLIC_URL
}

function getSupabaseAnonKey(): string {
  return SUPABASE_PUBLIC_ANON_KEY
}

function getSupabaseServiceKey(): string {
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY
  if (!key) {
    throw new Error('SUPABASE_SERVICE_ROLE_KEY is not defined')
  }
  return key
}

// -----------------------------------------------------------------------------
// Client Instances (Singleton Pattern)
// -----------------------------------------------------------------------------

let clientInstance: ReturnType<typeof createSupabaseClient<Database>> | null = null
let serverInstance: ReturnType<typeof createSupabaseClient<Database>> | null = null
let adminInstance: ReturnType<typeof createSupabaseClient<Database>> | null = null

// -----------------------------------------------------------------------------
// Fetch Timeout (10 seconds)
// -----------------------------------------------------------------------------
// Prevents hanging requests by aborting after 10s

function createFetchWithTimeout(timeoutMs: number = 10000) {
  return (url: RequestInfo | URL, options: RequestInit = {}) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    return fetch(url, { ...options, signal: controller.signal })
      .finally(() => clearTimeout(timeoutId))
  }
}

// -----------------------------------------------------------------------------
// Client-Side Supabase Client
// -----------------------------------------------------------------------------
// Use this in React components and client-side code
// Features: localStorage persistence, auto-refresh, session management

export function getSupabase() {
  if (!clientInstance) {
    // The legacy generic client stays inert on MyCIO pages; MyCIO uses getMyCIOClient
    // (its own getMyCIOClient) served from the same bundle/root layout. Left active here, this client
    // loads a leftover session and its autoRefresh loop POSTs to Supabase from
    // the mycio.ai origin -> CORS-blocked + 429 + infinite retry, which saturated the tab and froze the
    // MyCIO pages (20s stalls). On mycio.ai: no persisted session, no auto-refresh, no URL detection.
    const onMyCio =
      typeof window !== 'undefined' &&
      (/(?:^|\.)mycio\.ai$/i.test(window.location.hostname) || window.location.pathname.startsWith('/'))
    clientInstance = createSupabaseClient<Database>(
      getSupabaseUrl(),
      getSupabaseAnonKey(),
      {
        auth: {
          persistSession: !onMyCio,
          detectSessionInUrl: typeof window !== 'undefined' && !onMyCio,
          autoRefreshToken: !onMyCio,
          storage: typeof window !== 'undefined' ? window.localStorage : undefined,
          flowType: 'pkce',
        },
        global: {
          headers: {
            'x-client-info': 'mycio-web',
          },
          fetch: createFetchWithTimeout(10000),
        },
      }
    )
  }
  return clientInstance
}

// Proxy for lazy initialization - allows importing as constant
export const supabase = new Proxy({} as ReturnType<typeof getSupabase>, {
  get(target, prop) {
    return getSupabase()[prop as keyof ReturnType<typeof getSupabase>]
  },
})

// -----------------------------------------------------------------------------
// Server-Side Supabase Client
// -----------------------------------------------------------------------------
// Use this in API routes, server components, and server actions
// Features: No persistence, no auto-refresh (stateless)

export function getSupabaseServer() {
  if (!serverInstance) {
    serverInstance = createSupabaseClient<Database>(
      getSupabaseUrl(),
      getSupabaseAnonKey(),
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
        global: {
          headers: {
            'x-client-info': 'mycio-server',
          },
          fetch: createFetchWithTimeout(10000),
        },
      }
    )
  }
  return serverInstance
}

// Alias for backward compatibility
export const createServerClient = getSupabaseServer

// -----------------------------------------------------------------------------
// Admin Supabase Client (Service Role)
// -----------------------------------------------------------------------------
// Use this ONLY for admin operations that bypass RLS
// CAUTION: Service role key has full database access!

export function getSupabaseAdmin() {
  if (!adminInstance) {
    adminInstance = createSupabaseClient<Database>(
      getSupabaseUrl(),
      getSupabaseServiceKey(),
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
        global: {
          headers: {
            'x-client-info': 'mycio-admin',
          },
          fetch: createFetchWithTimeout(10000),
        },
      }
    )
  }
  return adminInstance
}

// Alias for backward compatibility
export const createAdminClient = getSupabaseAdmin

// -----------------------------------------------------------------------------
// Session & User Caching (5-minute TTL)
// -----------------------------------------------------------------------------
// Reduces API calls by caching session and user data

interface CachedData<T> {
  data: T
  timestamp: number
}

let sessionCache: CachedData<import('@supabase/supabase-js').Session | null> | null = null
let userCache: CachedData<unknown> | null = null

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

function isCacheValid<T>(cache: CachedData<T> | null): boolean {
  if (!cache) return false
  return Date.now() - cache.timestamp < CACHE_TTL
}

export async function getCachedSession() {
  if (isCacheValid(sessionCache)) {
    return sessionCache!.data
  }

  const client = getSupabase()
  const { data: { session } } = await client.auth.getSession()

  sessionCache = { data: session, timestamp: Date.now() }
  return session
}

export async function getCachedUser(token?: string) {
  if (isCacheValid(userCache)) {
    return { data: { user: userCache!.data }, error: null }
  }

  const client = getSupabase()
  const result = token
    ? await client.auth.getUser(token)
    : await client.auth.getUser()

  if (result.data.user) {
    userCache = { data: result.data.user, timestamp: Date.now() }
  }

  return result
}

// Clear cache on sign out
export async function signOut() {
  sessionCache = null
  userCache = null
  const client = getSupabase()
  return client.auth.signOut()
}

// -----------------------------------------------------------------------------
// Utility Functions
// -----------------------------------------------------------------------------

/**
 * Reset all client instances (useful for testing or after env var changes)
 */
export function resetSupabaseClients() {
  clientInstance = null
  serverInstance = null
  adminInstance = null
  sessionCache = null
  userCache = null
}

/**
 * Get user with automatic caching
 */
export async function getCurrentUser() {
  const result = await getCachedUser()
  return result.data.user
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getCachedSession()
  return session !== null
}

// -----------------------------------------------------------------------------
// Type Exports
// -----------------------------------------------------------------------------

export type { Database }
export type SupabaseClient = ReturnType<typeof getSupabase>
